/**
 * Which AG Grid filter an attribute column gets (BUG-5b / DV-405).
 *
 * Both grids used to give EVERY attribute column one control regardless of the
 * attribute's data type — the server grid an `agTextColumnFilter` restricted to
 * "equals", the client grid an `agSetColumnFilter`. So a Date attribute was
 * filtered by typing a date string into a text box, a Number attribute compared
 * lexically, and a Yes/No attribute offered whatever strings happened to be
 * loaded. The catalog already knows each attribute's {@link AttrDataType}; this
 * module turns that into the matching control.
 *
 * Two modes, because the two grids have different constraints:
 *
 * - `client` — AG Grid evaluates the predicate against loaded rows, so every
 *   operator the control offers actually works. Set filters can derive their
 *   values from the rows.
 * - `server` — the predicate is translated to a `VaultDocumentAttributeFilter`
 *   by `attributeFiltersFromFilterModel`, so a control may only offer what the
 *   backend's operator table accepts. This is NOT a soft constraint: an operator
 *   a value type doesn't allow raises `DocumentValidationError` server-side,
 *   which reaches the datasource as a GraphQL error and fails the block. The
 *   user gets a broken grid, not a wider result set.
 *
 * The table `server` mode encodes (document-vault-backend
 * `schemas/attribute_filter.py::ALLOWED_OPERATORS_BY_VALUE_TYPE`):
 *
 * | value type          | column       | operators                                    |
 * |---------------------|--------------|----------------------------------------------|
 * | text                | value_text   | equals, not_equal, contains, not_contains,   |
 * |                     |              | starts_with, ends_with, any_of, blank, …     |
 * | number              | value_number | equals, not_equal, <, <=, >, >=, in_range, … |
 * | date                | value_date   | equals, in_range, <, <=, >, >=, blank, …     |
 * | boolean             | value_boolean| equals ONLY                                  |
 * | single/multi_select | value_id     | any_of, blank, not_blank ONLY                |
 * | reference           | value_id     | any_of, blank, not_blank ONLY                |
 *
 * The last two rows are why this file changed shape. An id-backed attribute
 * cannot be filtered by typing text at all — its stored value is an opaque id,
 * and the name a user would type lives in another subgraph. Select attributes
 * get a real multi-select over the catalog's own option list (`any_of` resolves
 * each option's raw value to its option id); Client and Entity get the shared
 * `@jsq/common-components` typeaheads instead (see `referenceFilters.tsx`),
 * which is the same control `fund-admin-frontend`'s `data-vault/entities` grid
 * uses.
 */
import type { AgGrid } from "@jsq/ds/core";
import { dataTypeToValueType } from "#common/graphql/Space";
import type { AttrDataType } from "#common/types/vault";

/**
 * The subset of AG Grid filter params these controls use. `ColDef.filterParams`
 * is `any`, so naming the shape here is what keeps the call sites type-checked.
 */
export type AttributeFilterParams = {
  /** Operators offered in the control — never wider than the backend allows. */
  filterOptions?: string[];
  maxNumConditions?: number;
  /** Fixed option list for a set filter; omit to derive it from the rows. */
  values?: string[];
  browserDatePicker?: boolean;
  comparator?: (filterDate: Date, cellValue: unknown) => number;
  suppressMiniFilter?: boolean;
};

/** The `filter` / `filterParams` pair for one attribute column. */
export type AttributeFilterDef = {
  filter: string | false;
  filterParams?: AttributeFilterParams;
};

/** Where the predicate is evaluated — see the module header. */
export type FilterGridMode = "client" | "server";

/**
 * The two values a Yes/No attribute can hold, matching what `dashFormatter`
 * renders and what `isTruthyAttributeValue` accepts on the way to `boolean`.
 */
export const YES_NO_VALUES: readonly string[] = ["Yes", "No"];

/**
 * One `VaultDocumentAttributeFilter` carries one predicate, so no control may
 * build a combined (`AND`/`OR`) model — the translator drops those rather than
 * silently applying half of one.
 */
const ONE_CONDITION = { maxNumConditions: 1 } as const;

/** Text operators, in the backend's allow-list order. */
const SERVER_TEXT_OPERATORS: AttributeFilterParams = {
  filterOptions: [
    "equals",
    "notEqual",
    "contains",
    "notContains",
    "startsWith",
    "endsWith",
    "blank",
    "notBlank",
  ],
  ...ONE_CONDITION,
};

const SERVER_NUMBER_OPERATORS: AttributeFilterParams = {
  filterOptions: [
    "equals",
    "notEqual",
    "greaterThan",
    "greaterThanOrEqual",
    "lessThan",
    "lessThanOrEqual",
    "inRange",
    "blank",
    "notBlank",
  ],
  ...ONE_CONDITION,
};

/**
 * `not_equal` is absent on purpose: the backend's `_DATE_OPERATORS` omits it, so
 * offering "not equal" would produce a rejected filter. `greaterThanOrEqual` /
 * `lessThanOrEqual` are omitted because AG Grid's date filter doesn't offer them.
 */
const SERVER_DATE_OPERATORS: AttributeFilterParams = {
  filterOptions: ["equals", "lessThan", "greaterThan", "inRange", "blank", "notBlank"],
  ...ONE_CONDITION,
};

/** Column with no server-side predicate at all (no resolvable `attributeId`). */
export const NO_FILTER: AttributeFilterDef = { filter: false };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** Local midnight for a `Date`, as a comparable `YYYYMMDD`-ish number. */
const dayOrdinal = (y: number, m: number, d: number): number => y * 10000 + m * 100 + d;

/**
 * Compare a row's attribute value (a plain string — attribute values are stored
 * on the row as text) against the date the picker produced.
 *
 * `agDateColumnFilter` compares `Date` objects by default and treats a string
 * cell as "no value", which would make a date filter match nothing on the client
 * grid. Both sides are reduced to a calendar day so a value carrying a time
 * component still matches the day the user picked.
 */
export const dateCellComparator = (filterDate: Date, cellValue: unknown): number => {
  if (cellValue == null || cellValue === "") return -1;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- cellValue is an opaque grid cell value, defensively stringified for date parsing
  const parsed = new Date(String(cellValue));
  if (Number.isNaN(parsed.getTime())) return -1;
  const cell = dayOrdinal(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth() + 1,
    parsed.getUTCDate(),
  );
  const target = dayOrdinal(
    filterDate.getFullYear(),
    filterDate.getMonth() + 1,
    filterDate.getDate(),
  );
  if (cell === target) return 0;
  return cell < target ? -1 : 1;
};

export type AttributeFilterOptions = {
  mode: FilterGridMode;
  /**
   * The attribute's fixed value set, when one is known. Populate it and a select
   * attribute gets a real option list instead of a fallback — and on the server
   * grid that list is the ONLY way a select attribute becomes filterable, since
   * `any_of` resolves each value against the attribute's configured options and
   * rejects anything else.
   */
  values?: readonly string[] | null;
};

/**
 * The AG Grid filter for one attribute column.
 *
 * | data type          | client                    | server                        |
 * |--------------------|---------------------------|-------------------------------|
 * | Text               | agTextColumnFilter        | agTextColumnFilter (full set) |
 * | Number             | agNumberColumnFilter      | agNumberColumnFilter (+range) |
 * | Date               | agDateColumnFilter        | agDateColumnFilter (+range)   |
 * | Yes/No             | agSetColumnFilter [Yes,No]| agSetColumnFilter [Yes,No]    |
 * | Single/Multi-select| agSetColumnFilter         | agSetColumnFilter (options),  |
 * |                    | (options or row values)   | else no filter                |
 * | Reference          | agSetColumnFilter         | none here — see               |
 * |                    | (names from the rows)     | `referenceFilters.tsx`        |
 *
 * An unknown/absent `dataType` (a facet alias the catalog doesn't carry) falls
 * back to Text, which is what every column got before this change.
 */
export function attributeFilterDef(
  dataType: AttrDataType | undefined,
  { mode, values }: AttributeFilterOptions,
): AttributeFilterDef {
  const server = mode === "server";
  const text: AttributeFilterDef = {
    filter: "agTextColumnFilter",
    ...(server ? { filterParams: { ...SERVER_TEXT_OPERATORS } } : {}),
  };
  if (!dataType) return text;

  switch (dataTypeToValueType(dataType)) {
    case "number":
      return {
        filter: "agNumberColumnFilter",
        ...(server ? { filterParams: { ...SERVER_NUMBER_OPERATORS } } : {}),
      };

    case "date":
      return {
        filter: "agDateColumnFilter",
        filterParams: {
          ...(server ? SERVER_DATE_OPERATORS : {}),
          browserDatePicker: true,
          // Row values are strings; without this the filter matches nothing.
          comparator: dateCellComparator,
        },
      };

    case "boolean":
      // A closed two-value list, and the one set-filtered type the backend
      // restricts to plain `equals`. The only models it can emit are "one value
      // selected" (an equality) and "everything selected" (AG Grid emits no
      // model at all), so both round-trip.
      return {
        filter: "agSetColumnFilter",
        filterParams: { values: [...YES_NO_VALUES], suppressMiniFilter: true },
      };

    case "reference":
      // A reference value is an opaque id whose display name lives in another
      // subgraph, so on the server grid no stock control can produce a matching
      // predicate — Client and Entity are handed the typeahead filters in
      // `referenceFilters.tsx` by the caller, and any other reference attribute
      // (Investor / LP) has no picker to offer yet, so it gets none rather than
      // a box that provably matches nothing. The client grid renders resolved
      // NAMES in the cell, so a set filter over the loaded rows works there.
      return server ? NO_FILTER : { filter: "agSetColumnFilter" };

    case "single_select":
    case "multi_select": {
      // `any_of` is the only operator these accept, and an AG Grid set filter is
      // exactly an `any_of` producer — but only over the attribute's CONFIGURED
      // options: the backend resolves each ticked value to an option id and
      // rejects a value that matches none.
      if (values?.length) {
        return { filter: "agSetColumnFilter", filterParams: { values: [...values] } };
      }
      // No option list to offer. On the client grid AG Grid can derive one from
      // the loaded rows, which is complete because it holds every row. On the
      // server grid there is nothing to derive from and no operator other than
      // `any_of`, so the honest answer is no filter.
      return server ? NO_FILTER : { filter: "agSetColumnFilter" };
    }

    // text
    default:
      return text;
  }
}

/** Matches a leading calendar day in an AG Grid `dateFrom` / `dateTo` string. */
const LEADING_DAY = /^(\d{4}-\d{2}-\d{2})/;

/**
 * Trim `agDateColumnFilter`'s `dateFrom`/`dateTo` to a calendar day.
 *
 * AG Grid serializes a picked date as `"2026-03-15 00:00:00"`, and the filter's
 * `date` is a raw `String` on the wire (no scalar validation), so a bad shape
 * doesn't error — it just matches nothing.
 *
 * The translator now normalizes this itself (`asIsoDay` in
 * `#common/graphql/Document/mappers`), which is where it belongs. This stays as
 * a defensive, idempotent pass for callers that inspect or persist a filter
 * model before it reaches the mapper.
 */
export function normalizeDateFilterModel(
  filterModel: AgGrid.FilterModel | null | undefined,
): AgGrid.FilterModel | null | undefined {
  if (!filterModel) return filterModel;
  let changed = false;
  const next: Record<string, unknown> = {};
  Object.entries(filterModel).forEach(([colId, model]) => {
    if (isObject(model) && model.filterType === "date") {
      const trimmed: Record<string, unknown> = { ...model };
      (["dateFrom", "dateTo"] as const).forEach((field) => {
        const raw = model[field];
        if (typeof raw !== "string") return;
        const day = LEADING_DAY.exec(raw)?.[1];
        if (day && day !== raw) {
          trimmed[field] = day;
          changed = true;
        }
      });
      next[colId] = trimmed;
      return;
    }
    next[colId] = model;
  });
  return changed ? next : filterModel;
}
